// Issue #363: Clientseitige, automatische Bildkomprimierung.
//
// Statt zu grosse Bilder beim Upload abzulehnen, werden sie hier iterativ
// (kleinere Kantenlaenge und/oder JPEG-Qualitaet) komprimiert, bis sie unter
// das Groessenlimit passen. Genutzt von ImagePicker (Profilbild/Logo) und
// dem Onboarding-Logo-Schritt.

export interface CompressResult {
  /** Datei nach (ggf.) Komprimierung. */
  file: File;
  /** true, wenn das Bild komprimiert wurde (sonst Original unveraendert). */
  compressed: boolean;
}

/** Laedt eine Datei als dekodiertes <img>-Element. */
function loadImageEl(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Bild konnte nicht geladen werden"));
    };
    img.src = url;
  });
}

/** Rendert das Bild skaliert (laengste Kante <= maxEdge) in einen Blob. */
function renderBlob(
  img: HTMLImageElement,
  maxEdge: number,
  type: string,
  quality?: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const width = img.width;
    const height = img.height;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve(null);
      return;
    }
    // JPEG kennt keine Transparenz -> weisser Hintergrund.
    if (type === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/**
 * Komprimiert ein Bild iterativ, bis es unter maxBytes liegt. PNGs werden
 * zunaechst als PNG verkleinert (Transparenz bleibt erhalten); reicht das
 * nicht, wird auf JPEG ausgewichen. Vektorformate (z. B. SVG) und bereits
 * kleine Dateien werden unveraendert zurueckgegeben.
 *
 * Wirft, wenn das Bild nicht dekodiert werden kann.
 */
export async function compressImageToLimit(file: File, maxBytes: number): Promise<CompressResult> {
  // Schon klein genug oder nicht rasterisierbar -> unveraendert uebernehmen.
  if (file.size <= maxBytes || (file.type !== "image/png" && file.type !== "image/jpeg")) {
    return { file, compressed: false };
  }

  const img = await loadImageEl(file);
  const origMaxEdge = Math.max(img.width, img.height);
  const cappedStart = Math.min(origMaxEdge, 2048);
  const edges = [...new Set([cappedStart, 1600, 1280, 1024, 800, 640, 512].filter((e) => e <= cappedStart))].sort(
    (a, b) => b - a,
  );
  const baseName = (file.name || "bild").replace(/\.[^.]+$/, "");
  const makeFile = (blob: Blob, type: string): File => {
    const ext = type === "image/png" ? "png" : "jpg";
    return new File([blob], `${baseName}.${ext}`, { type });
  };

  // Holder-Objekt, damit die Closure die Variable mutieren kann, ohne dass die
  // TS-Flussanalyse den Typ faelschlich auf null verengt.
  const state: { best: { blob: Blob; type: string } | null } = { best: null };
  const track = (blob: Blob | null, type: string) => {
    if (blob && (!state.best || blob.size < state.best.blob.size)) state.best = { blob, type };
  };

  // 1) PNG-Quelle: erst verlustfrei verkleinern (Transparenz erhalten).
  if (file.type === "image/png") {
    for (const edge of edges) {
      const blob = await renderBlob(img, edge, "image/png");
      track(blob, "image/png");
      if (blob && blob.size <= maxBytes) {
        return { file: makeFile(blob, "image/png"), compressed: true };
      }
    }
  }

  // 2) JPEG mit absteigender Qualitaet und Kantenlaenge.
  const qualities = [0.85, 0.72, 0.6, 0.45, 0.35];
  for (const edge of edges) {
    for (const q of qualities) {
      const blob = await renderBlob(img, edge, "image/jpeg", q);
      track(blob, "image/jpeg");
      if (blob && blob.size <= maxBytes) {
        return { file: makeFile(blob, "image/jpeg"), compressed: true };
      }
    }
  }

  // 3) Nichts passt exakt -> kleinste Variante zurueckgeben (Aufrufer prueft
  //    danach erneut und zeigt ggf. einen Fehler).
  if (state.best) {
    return { file: makeFile(state.best.blob, state.best.type), compressed: true };
  }
  return { file, compressed: false };
}
